/**
 * Spot Dimension Tool — Annotate > Dimension panel spot tools.
 *
 * One parameterized tool for the three spot annotations:
 *   elevation  (EL) — absolute/relative height of a picked point,
 *                     triangle head + horizontal text flag (+3.500)
 *   coordinate     — project North/East coordinates of a point (N/E text)
 *   slope          — surface slope readout (%) with the slope triangle
 *
 * BIM workflow: click a host point/surface, drag the flag out, click again
 * to drop it. The tool supports the full click → drag → drop cycle.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import i18n from '../i18n';

export type SpotKind = 'elevation' | 'coordinate' | 'slope';

export interface SpotDimensionGeometryData {
    kind: SpotKind;
    position: [number, number, number];
    /** Where the text flag ended up (drag target). */
    flagPos: [number, number, number];
    /** Direction the leader points from the flag to the target (deg). */
    leaderAngle: number;
}

export interface SpotDimensionElement extends Element {
    category: 'Dimensions';
    geometryData: SpotDimensionGeometryData;
}

const KIND_LABEL: Record<SpotKind, string> = {
    elevation: 'Spot Elevation',
    coordinate: 'Spot Coordinate',
    slope: 'Spot Slope',
};

export class SpotDimensionTool extends BaseTool {
    id = 'spot_dimension';
    name: string;
    cursor = 'crosshair';

    private kind: SpotKind;
    /** First click: the measured point. Null while awaiting it. */
    private anchor: THREE.Vector3 | null = null;
    /** Surface normal sampled at the anchor (used by the slope readout). */
    private anchorNormal: THREE.Vector3 | null = null;
    private current: THREE.Vector3 | null = null;

    constructor(kind: SpotKind = 'elevation') {
        super();
        this.kind = kind;
        this.id = `spot_${kind}`;
        this.name = KIND_LABEL[kind];
    }

    protected onActivate(): void {
        this.anchor = null;
        this.current = null;
        this.notify(i18n.t('tools.notify.spotDimClick', { kind: KIND_LABEL[this.kind] }));
    }

    /** Anchor pick: prefer the real model surface under the cursor (its Z
     *  matters for spot elevations/slopes), fall back to grid-snapped work
     *  plane. A pure plane snap would flatten every anchor to Z=0. */
    private snapAnchor(event: ThreeEvent<PointerEvent>): THREE.Vector3 | null {
        const hits = this.raycastSceneAtEvent(event);
        for (const hit of hits) {
            if (!hit.object.visible || hit.object.userData?.isEventPlane === true) continue;
            return hit.point.clone();
        }
        return this.snapPlane(event);
    }

    /** Work-plane pick for the flag position (grid snapped). */
    private snapPlane(event: ThreeEvent<PointerEvent>): THREE.Vector3 | null {
        const point = this.getIntersection(event);
        if (!point) return null;
        return this.snapToGrid(point, 0.1);
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.anchor ? this.snapPlane(event) : this.snapAnchor(event);
        if (!point) return;

        if (!this.anchor) {
            this.anchor = point;
            this.current = point;
            // Sample the surface normal under the click so the slope spot
            // reports the real inclination of the host face.
            this.anchorNormal = this.faceNormalAt(event);
        } else {
            this.createSpot(point);
            this.anchor = null;
            this.anchorNormal = null;
            this.current = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.snapPlane(event);
        if (!point) return;
        this.current = point;
        this.context.appStore.setToolPreview({
            type: 'spot_dimension',
            kind: this.kind,
            anchor: this.anchor,
            current: point,
            value: this.readout(point),
        });
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape' && this.anchor) {
            this.anchor = null;
            this.current = null;
            this.context?.appStore.setToolPreview(null);
            return;
        }
        super.onKeyDown(event);
    }

    /** Human-readable value for the measured anchor point. */
    private readout(place: THREE.Vector3): string {
        const p = this.anchor ?? place;
        switch (this.kind) {
            case 'elevation': {
                // Options Bar mode (Absolute / Relative to Base Level /
                // Shared): relative reads against the nearest level at or
                // below the anchor elevation.
                const elev = p.z - this.baseLevelElevation(p.z);
                const sign = elev >= 0 ? '+' : '';
                return `${sign}${elev.toFixed(3)}`;
            }
            case 'coordinate': {
                // World axes: Y = North, X = East.
                return `N ${(p.y * 1000).toFixed(0)}  E ${(p.x * 1000).toFixed(0)}`;
            }
            case 'slope': {
                // Sample the surface slope under the anchor via its normal.
                const pct = this.slopePercentAt(p);
                return `${pct.toFixed(1)}%`;
            }
        }
    }

    /**
     * Base-level elevation (m) the spot elevation measures from, honoring the
     * Options Bar 'spotElevationType': Absolute → 0; Relative → the highest
     * project level whose elevation is <= the anchor; Shared → the base
     * object elevation of the host (falls back to relative base).
     */
    private baseLevelElevation(anchorZ: number): number {
        const mode = this.context?.bimStore.toolProperties.spotElevationType ?? 'Absolute';
        if (mode === 'Absolute' || !this.context) return 0;
        // The datum map stores level elevation in millimeters (LevelTool,
        // BIMKernel seed and LevelRenderer all follow the mm convention).
        const levels = Array.from(this.context.bimStore.project.levels.values())
            .map((l) => ({ id: l.id, elevationM: l.elevation / 1000 }));
        const base = levels
            .filter((l) => l.elevationM <= anchorZ + 1e-6)
            .sort((a, b) => b.elevationM - a.elevationM)[0];
        return base?.elevationM ?? 0;
    }

    /**
     * Slope (%) of the face sampled at the anchor: rise/run = tan of the
     * angle between the face normal and vertical. A flat floor reads 0%,
     * a 45° ramp 100%. Falls back to the drag elevation delta when no
     * surface normal was captured (e.g. clicking the ground plane).
     */
    private slopePercentAt(p: THREE.Vector3): number {
        if (this.anchorNormal) {
            const n = this.anchorNormal.clone().normalize();
            // Normal may point down; use its absolute vertical component.
            const vert = Math.abs(n.z);
            if (vert > 1e-4) {
                const horizontal = Math.sqrt(Math.max(1 - vert * vert, 0));
                return THREE.MathUtils.clamp((horizontal / vert) * 100, 0, 1000);
            }
            return 1000; // vertical face
        }
        if (!this.current) return 0;
        const d = this.current.clone().sub(p);
        const run = Math.max(Math.hypot(d.x, d.y), 0.001);
        return THREE.MathUtils.clamp((Math.abs(d.z) / run) * 100, 0, 1000);
    }

    /** World-space face normal of the topmost visible object under the pointer. */
    private faceNormalAt(event: ThreeEvent<PointerEvent>): THREE.Vector3 | null {
        if (!this.context) return null;
        const hits = this.raycastSceneAtEvent(event);
        for (const hit of hits) {
            if (!hit.object.visible || hit.object.userData?.isEventPlane === true) continue;
            if (!hit.normal) continue;
            const normal = hit.normal.clone();
            // hit.normal is in object space for some geometries; transform it.
            const el = hit.object as THREE.Mesh;
            const m = new THREE.Matrix3().getNormalMatrix(el.matrixWorld);
            normal.applyMatrix3(m).normalize();
            return normal;
        }
        return null;
    }

    private createSpot(place: THREE.Vector3) {
        if (!this.context || !this.anchor) return;

        const value = this.readout(place);
        const d = place.clone().sub(this.anchor);
        const leaderAngle = THREE.MathUtils.radToDeg(Math.atan2(d.y, d.x));

        const geometryData: SpotDimensionGeometryData = {
            kind: this.kind,
            position: [this.anchor.x, this.anchor.y, this.anchor.z],
            flagPos: [place.x, place.y, place.z],
            leaderAngle,
        };

        const params: Record<string, any> = { 'Value': value, 'Kind': KIND_LABEL[this.kind] };
        if (this.kind === 'elevation') {
            params['Elevation'] = `${value} m`;
            const mode = this.context.bimStore.toolProperties.spotElevationType ?? 'Absolute';
            params['Relative to Base Level'] = mode !== 'Absolute';
        } else if (this.kind === 'coordinate') {
            params['North'] = (this.anchor.y * 1000).toFixed(0);
            params['East'] = (this.anchor.x * 1000).toFixed(0);
        } else {
            params['Slope'] = value;
        }

        const spot: SpotDimensionElement = {
            id: uuidv4(),
            category: 'Dimensions',
            type: KIND_LABEL[this.kind],
            parameters: params,
            constraints: [],
            geometryData,
        };

        this.context.bimStore.addElement(spot);
    }
}
